/**
 * CopilotChat — shared chat surface used by both the right-side
 * CopilotPanel (mounted globally in `_layout.tsx`) and the full-page
 * `/app/twin/copilot` route. Reads conversation state from the global
 * `CopilotProvider`, dispatches accept clicks through
 * `lib/copilot/dispatch.ts`.
 *
 * Renders:
 *   - Message list (user + assistant bubbles, citations, proposal cards)
 *   - "Accept all" button on assistant turns with ≥ 2 unresolved proposals
 *   - Per-proposal Accept / Edit / Reject buttons
 *   - Inline validation error (red card + disabled Accept) when the
 *     proposal's payload fails `validateProposal()`
 *   - Footer: input + send button
 */

import { useMemo } from "react";
import { format } from "date-fns";
import {
  ArrowUp,
  Bot,
  Check,
  ChevronRight,
  Edit3,
  HelpCircle,
  Sparkles,
  User,
  X,
} from "lucide-react";
import { Badge } from "@/lib/ui/badge";
import { Button } from "@/lib/ui/button";
import { Input } from "@/lib/ui/input";
import { cn } from "@/lib/ui/cn";
import {
  type CopilotMessage,
  type ProposalApply,
} from "@/lib/widgets/__foundry_fixtures";
import { useCopilot } from "@/lib/copilot/store";
import { COPILOT_COMING_SOON } from "@/lib/copilot/flags";
import { useToast } from "@/lib/ui/toast";
import { usePlant } from "@/lib/plant/plant-store";
import { useComputedTags } from "@/lib/compute/store";
import { useDashboards } from "@/lib/dashboard/store";
import { useAlarms } from "@/lib/alarms/store";
import {
  applyProposal,
  validateProposal,
  type DispatchStores,
} from "@/lib/copilot/dispatch";
import { generateAuthoringTurn } from "@/lib/copilot/context";

const PROPOSAL_ICON = {
  entity: Sparkles,
  connection: ChevronRight,
  correlation_rule: Sparkles,
  clarifying_question: HelpCircle,
} as const;

export interface CopilotChatProps {
  /** Optional draft / input state passed from the host. */
  draft: string;
  onDraftChange: (next: string) => void;
  /** Render mode — affects bubble max-width + outer padding. */
  mode?: "panel" | "full";
  /** Optional context from the URL (used by `/app/twin/copilot`). */
  ctxKind?: string | null;
}

export function CopilotChat({
  draft,
  onDraftChange,
  mode = "panel",
  ctxKind = null,
}: CopilotChatProps) {
  const copilot = useCopilot();
  const plant = usePlant();
  const computed = useComputedTags();
  const dashboards = useDashboards();
  const alarms = useAlarms();
  const toast = useToast();

  const stores = useMemo<DispatchStores>(
    () => ({ plant, computed, dashboards, alarms, computedTags: computed }),
    [plant, computed, dashboards, alarms],
  );

  const isAuthoring = ctxKind === "model_authoring";

  const submit = (override?: string) => {
    // Demo gating (#117): the copilot is "Coming soon" — swallow all sends.
    if (COPILOT_COMING_SOON) return;
    const userText = (override ?? draft).trim();
    if (!userText) return;
    const userMsg: CopilotMessage = {
      id: `m-u-${Date.now()}`,
      role: "user",
      content: userText,
      timestamp: new Date().toISOString(),
    };

    let assistantMsg: CopilotMessage;
    const authoring = isAuthoring ? generateAuthoringTurn(userText) : null;
    if (authoring) {
      assistantMsg = authoring;
    } else if (isAuthoring) {
      assistantMsg = {
        id: `m-a-${Date.now()}`,
        role: "assistant",
        content:
          "I don't have a canned proposal for that yet — Phase 4 wires this turn to a real Claude call. Try \"Add a second induction furnace\", \"Add a tilt furnace\", \"Add a second moulding line\", or \"Add a shot blast cabinet\".",
        timestamp: new Date().toISOString(),
      };
    } else {
      // Page-context-aware intent matching (B0.43). Falls back to generic.
      const contextTurn = generatePageContextTurn(userText, copilot.pageContext);
      assistantMsg =
        contextTurn ??
        ({
          id: `m-a-${Date.now()}`,
          role: "assistant",
          content:
            "Phase 0 — this turn is canned. In production it's grounded in your tag stream + entity catalog + pack corpus. Try \"add a moisture gauge to this dashboard\" or \"alert when bath_temp > 1500 for 30s\".",
          timestamp: new Date().toISOString(),
        } as CopilotMessage);
    }
    copilot.appendMessages([userMsg, assistantMsg]);
    onDraftChange("");
  };

  const handleAccept = (proposal: NonNullable<CopilotMessage["proposals"]>[number]) => {
    if (!proposal.apply) {
      copilot.setDecision(proposal.id, "accepted");
      return;
    }
    const result = applyProposal(proposal.apply, stores, {
      proposalId: proposal.id,
      aliasMap: copilot.aliasMap,
      onAlias: (placeholder, realId) => copilot.setAlias(placeholder, realId),
    });
    if (!result.ok) {
      toast.push("error", result.reason ?? "Proposal couldn't be applied");
      return;
    }
    toast.push("ok", result.summary ?? "Applied");
    copilot.setDecision(proposal.id, "accepted");
  };

  const handleAcceptAll = (msg: CopilotMessage) => {
    if (!msg.proposals) return;
    const undecided = msg.proposals.filter((p) => !copilot.decided[p.id] && p.apply);
    let ok = 0;
    let failed = 0;
    for (const p of undecided) {
      if (!p.apply) continue;
      const result = applyProposal(p.apply, stores, {
        proposalId: p.id,
        aliasMap: copilot.aliasMap,
        onAlias: (placeholder, realId) => copilot.setAlias(placeholder, realId),
      });
      if (result.ok) {
        copilot.setDecision(p.id, "accepted");
        ok++;
      } else {
        failed++;
      }
    }
    if (ok > 0 && failed === 0) {
      toast.push("ok", `Accepted ${ok} proposal${ok === 1 ? "" : "s"}`);
    } else if (ok > 0 && failed > 0) {
      toast.push(
        "info",
        `Accepted ${ok} · ${failed} failed (see proposal cards)`,
      );
    } else if (failed > 0) {
      toast.push("error", `${failed} proposal${failed === 1 ? "" : "s"} couldn't be applied`);
    }
  };

  const handleReject = (proposalId: string) => {
    copilot.setDecision(proposalId, "rejected");
  };

  return (
    <div className="flex h-full flex-col">
      <div
        className={cn(
          "flex flex-1 flex-col gap-4 overflow-y-auto p-4",
          mode === "full" && "p-5",
        )}
      >
        {copilot.messages.map((m) =>
          m.role === "user" ? (
            <UserBubble key={m.id} message={m} mode={mode} />
          ) : (
            <AssistantBubble
              key={m.id}
              message={m}
              decided={copilot.decided}
              stores={stores}
              mode={mode}
              onAccept={handleAccept}
              onAcceptAll={() => handleAcceptAll(m)}
              onReject={handleReject}
            />
          ),
        )}
      </div>
      <div className="border-t border-border p-3">
        <div className="flex items-center gap-2">
          <Input
            placeholder={
              COPILOT_COMING_SOON
                ? "AI Casting Consultant chat is coming soon…"
                : "Ask the AI Casting Consultant — e.g. 'add a moisture gauge to this dashboard'…"
            }
            value={COPILOT_COMING_SOON ? "" : draft}
            disabled={COPILOT_COMING_SOON}
            aria-label="AI Casting Consultant message"
            onChange={(e) => onDraftChange(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                submit();
              }
            }}
          />
          <Button
            size="icon"
            aria-label="Send"
            disabled={COPILOT_COMING_SOON}
            onClick={() => submit()}
          >
            <ArrowUp className="size-4" />
          </Button>
        </div>
        <p className="mt-1.5 text-[11px] text-on-surface-muted">
          {COPILOT_COMING_SOON
            ? "AI Casting Consultant is coming soon: chat is not open for this organisation yet."
            : "⏎ to send · Shift+⏎ for a new line."}
        </p>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// Page-context-aware intent matcher
// ────────────────────────────────────────────────────────────────────

/**
 * Pattern-match the user's input against a few simple intents that
 * reference *this* page's context — "add a gauge to this dashboard",
 * "alert when foo > 5 for 30s", etc. Phase 0 hand-rolled; Phase 4
 * Claude tool-call.
 *
 * Returns null if no pattern matches.
 */
function generatePageContextTurn(
  userText: string,
  pageContext: ReturnType<typeof useCopilot>["pageContext"],
): CopilotMessage | null {
  const text = userText.toLowerCase().trim();
  const now = new Date().toISOString();
  const dashboardId = pageContext?.currentDashboardId;

  // "alarm me if X > Y" / "alert when X > Y for Z seconds"
  const alarmMatch = text.match(
    /(?:alarm me|alert(?:\s+me)?(?:\s+when)?|warn(?:\s+me)?(?:\s+when)?)\s+(?:if\s+)?(\w[\w_]*)\s*(>=|<=|>|<|=)\s*(-?\d+(?:\.\d+)?)(?:\s+for\s+(\d+)\s*(?:s|sec|secs|seconds)?)?/i,
  );
  if (alarmMatch) {
    const [, tag, op, valueStr, forStr] = alarmMatch;
    const opMap: Record<string, "gt" | "lt" | "gte" | "lte" | "eq"> = {
      ">": "gt",
      "<": "lt",
      ">=": "gte",
      "<=": "lte",
      "=": "eq",
    };
    const value = parseFloat(valueStr);
    const forSeconds = forStr ? parseInt(forStr, 10) : undefined;
    const condition = {
      op: opMap[op] ?? "gt",
      value,
      ...(forSeconds ? { forSeconds } : {}),
    };
    return {
      id: `m-a-${Date.now()}`,
      role: "assistant",
      content: `I'll create an alarm rule on \`${tag}\` ${op} ${value}${
        forSeconds ? ` for ${forSeconds}s` : ""
      }. You can tune the severity + channels before saving.`,
      timestamp: now,
      proposals: [
        {
          id: `p-${Date.now()}-1`,
          kind: "correlation_rule",
          title: `Create alarm: ${tag} ${op} ${value}${forSeconds ? ` for ${forSeconds}s` : ""}`,
          rationale: `Fires a warning when ${tag} crosses the threshold.${
            forSeconds ? ` Hold time prevents nuisance trips.` : ""
          }`,
          actions: ["accept", "reject"],
          apply: {
            type: "create_alarm_rule",
            name: `${tag} alarm`,
            targetTagId: tag,
            targetLabel: tag,
            condition,
            severity: "warning",
            channels: ["in-app"],
            enabled: true,
          },
        },
      ],
    };
  }

  // "add a gauge / kpi / timeseries / spc for X to this dashboard"
  const widgetMatch = text.match(
    /(?:add(?:\s+a)?)\s+(gauge|kpi|timeseries|time\s*series|spc|histogram|alarm.?list|alarm-list)(?:\s+for\s+(\w[\w_]*))?(?:\s+to\s+(?:this\s+dashboard|the\s+dashboard))?/i,
  );
  if (widgetMatch && dashboardId) {
    const [, kindRaw, tag] = widgetMatch;
    const kindMap: Record<string, string> = {
      gauge: "gauge",
      kpi: "kpi",
      timeseries: "timeseries",
      "time series": "timeseries",
      spc: "spc",
      histogram: "histogram",
      "alarm list": "alarm-list",
      "alarm-list": "alarm-list",
      alarmlist: "alarm-list",
    };
    const widgetType = kindMap[kindRaw.toLowerCase()] ?? "kpi";
    const proposals: NonNullable<CopilotMessage["proposals"]> = [
      {
        id: `p-${Date.now()}-1`,
        kind: "entity",
        title: `Add ${widgetType} widget${tag ? ` for ${tag}` : ""}`,
        rationale: `Dropped at the bottom of "${pageContext?.label ?? "this dashboard"}". Bind it to a tag in step 2.`,
        actions: ["accept", "reject"],
        apply: {
          type: "add_widget",
          dashboardId,
          widget: {
            type: widgetType,
            title: tag ? `${tag} (${widgetType})` : `New ${widgetType}`,
          },
        },
      },
    ];
    if (tag) {
      // Hint: the user has named the tag; we can't bind it in a single
      // step (the new widget id isn't known until after add_widget runs)
      // without a multi-step "wait for accept" plan — Phase 4 territory.
      // We surface a clarifying question instead.
      proposals.push({
        id: `p-${Date.now()}-2`,
        kind: "clarifying_question",
        title: `After it's added, bind ${tag} to the value slot from the widget config?`,
        actions: ["accept"],
      });
    }
    return {
      id: `m-a-${Date.now()}`,
      role: "assistant",
      content: `Adding a ${widgetType}${tag ? ` for \`${tag}\`` : ""} to ${
        pageContext?.label ?? "this dashboard"
      }. Accept the proposal to drop it in.`,
      timestamp: now,
      proposals,
    };
  }

  // "rename / set title of widget X to Y"
  const renameMatch = text.match(
    /rename\s+(?:the\s+)?(?:widget\s+)?["']?([\w\s-]+?)["']?\s+to\s+["']?(.+?)["']?$/i,
  );
  if (renameMatch && dashboardId) {
    const [, oldTitle, newTitle] = renameMatch;
    return {
      id: `m-a-${Date.now()}`,
      role: "assistant",
      content: `I'll rename the widget matching "${oldTitle}" to "${newTitle}". If the validator flags multiple matches I'll list them.`,
      timestamp: now,
      proposals: [
        {
          id: `p-${Date.now()}-1`,
          kind: "entity",
          title: `Rename "${oldTitle}" → "${newTitle}"`,
          actions: ["accept", "reject"],
          // We don't know the widgetId here; the validator will reject
          // unless the user is on a dashboard with a uniquely-titled
          // widget. This is the seam where Phase 4's tool call resolves
          // the id from the title.
          apply: {
            type: "set_widget_title",
            dashboardId,
            widgetId: oldTitle.trim(),
            title: newTitle.trim(),
          },
        },
      ],
    };
  }

  // "clone this dashboard"
  if (/clone\s+(?:this\s+)?dashboard/.test(text) && dashboardId) {
    return {
      id: `m-a-${Date.now()}`,
      role: "assistant",
      content: `Cloning "${pageContext?.label ?? "this dashboard"}" into a new editable copy.`,
      timestamp: now,
      proposals: [
        {
          id: `p-${Date.now()}-1`,
          kind: "entity",
          title: `Clone "${pageContext?.label ?? "this dashboard"}"`,
          actions: ["accept", "reject"],
          apply: { type: "clone_dashboard", sourceId: dashboardId },
        },
      ],
    };
  }

  // "create / make / build a (foo) dashboard"
  const newDashboardMatch = text.match(
    /(?:create|make|build|new)(?:\s+me)?(?:\s+a)?\s+([\w\s-]+?)\s+dashboard/i,
  );
  if (newDashboardMatch) {
    const [, name] = newDashboardMatch;
    const trimmed = name.trim();
    return {
      id: `m-a-${Date.now()}`,
      role: "assistant",
      content: `Creating a "${trimmed}" dashboard. After it's created you can add + bind widgets — try "add a moisture gauge to this dashboard" once you're on it.`,
      timestamp: now,
      proposals: [
        {
          id: `p-${Date.now()}-1`,
          kind: "entity",
          title: `Create "${trimmed}" dashboard`,
          actions: ["accept", "reject"],
          apply: {
            type: "create_dashboard",
            name: trimmed.charAt(0).toUpperCase() + trimmed.slice(1),
          },
        },
      ],
    };
  }

  return null;
}

// ────────────────────────────────────────────────────────────────────
// Bubbles
// ────────────────────────────────────────────────────────────────────

function UserBubble({
  message,
  mode,
}: {
  message: CopilotMessage;
  mode: "panel" | "full";
}) {
  return (
    <div className="flex justify-end gap-2">
      <div className={cn("flex flex-col gap-1", mode === "panel" ? "max-w-xs" : "max-w-2xl")}>
        <div className="rounded-2xl rounded-br-md bg-brand px-3 py-2 text-sm text-brand-foreground">
          {message.content}
        </div>
        <span className="text-right text-[10px] text-on-surface-muted">
          {format(new Date(message.timestamp), "HH:mm")}
        </span>
      </div>
      <div className="flex size-7 shrink-0 items-center justify-center rounded-full bg-surface-muted text-on-surface">
        <User className="size-3.5" />
      </div>
    </div>
  );
}

function AssistantBubble({
  message,
  decided,
  stores,
  mode,
  onAccept,
  onAcceptAll,
  onReject,
}: {
  message: CopilotMessage;
  decided: Record<string, "accepted" | "rejected">;
  stores: DispatchStores;
  mode: "panel" | "full";
  onAccept: (p: NonNullable<CopilotMessage["proposals"]>[number]) => void;
  onAcceptAll: () => void;
  onReject: (proposalId: string) => void;
}) {
  const undecided = (message.proposals ?? []).filter(
    (p) => !decided[p.id] && p.apply,
  );
  const showAcceptAll = undecided.length >= 2;

  return (
    <div className="flex gap-2">
      <div className="flex size-7 shrink-0 items-center justify-center rounded-full bg-brand text-brand-foreground">
        <Bot className="size-3.5" />
      </div>
      <div className={cn("flex flex-col gap-2", mode === "panel" ? "max-w-xs" : "max-w-2xl")}>
        {message.content && (
          <div className="rounded-2xl rounded-bl-md bg-surface px-3 py-2 text-sm text-on-surface">
            {message.content}
          </div>
        )}
        {showAcceptAll && (
          <Button size="sm" onClick={onAcceptAll} className="self-start">
            <Check className="mr-1 size-3.5" />
            Accept all {undecided.length}
          </Button>
        )}
        {message.proposals?.map((p) => (
          <ProposalCard
            key={p.id}
            proposal={p}
            decision={decided[p.id]}
            stores={stores}
            onAccept={() => onAccept(p)}
            onReject={() => onReject(p.id)}
          />
        ))}
        <span className="text-[10px] text-on-surface-muted">
          {format(new Date(message.timestamp), "HH:mm")}
        </span>
      </div>
    </div>
  );
}

function ProposalCard({
  proposal,
  decision,
  stores,
  onAccept,
  onReject,
}: {
  proposal: NonNullable<CopilotMessage["proposals"]>[number];
  decision: "accepted" | "rejected" | undefined;
  stores: DispatchStores;
  onAccept: () => void;
  onReject: () => void;
}) {
  const Icon = PROPOSAL_ICON[proposal.kind];
  const validation = proposal.apply
    ? validateProposal(proposal.apply as ProposalApply, stores)
    : { ok: true };
  const valid = validation.ok;

  return (
    <div
      className={cn(
        "rounded-lg border bg-surface p-2.5 transition-colors",
        decision === "accepted"
          ? "border-success/40 bg-success/5"
          : decision === "rejected"
            ? "border-danger/40 bg-danger/5 opacity-70"
            : !valid
              ? "border-danger/40 bg-danger/5"
              : "border-border",
      )}
    >
      <div className="flex items-start gap-2">
        <div className="mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-md bg-brand-muted text-brand">
          <Icon className="size-3.5" />
        </div>
        <div className="flex flex-1 flex-col gap-1.5">
          <div className="flex items-baseline justify-between gap-2">
            <span className="text-xs font-semibold text-on-surface">
              {proposal.title}
            </span>
            <Badge variant="secondary" className="text-[9px] uppercase">
              {proposal.kind.replace(/_/g, " ")}
            </Badge>
          </div>
          {proposal.rationale && (
            <p className="text-[11px] text-on-surface-muted">{proposal.rationale}</p>
          )}
          {!valid && validation.reason && (
            <p className="rounded border border-danger/40 bg-danger/10 px-2 py-1 text-[11px] text-danger">
              ⚠ {validation.reason}
            </p>
          )}
          {proposal.citations && proposal.citations.length > 0 && (
            <div className="flex flex-col gap-0.5 rounded-md border border-border bg-surface-muted/50 p-1.5">
              <span className="text-[9px] font-semibold uppercase tracking-wide text-on-surface-muted">
                Citations
              </span>
              {proposal.citations.map((c, idx) => (
                <p key={idx} className="text-[10px] text-on-surface-muted">
                  <span className="font-mono">{c.type}</span>: {c.ref}
                </p>
              ))}
            </div>
          )}
          {!decision && (
            <div className="flex flex-wrap gap-1.5">
              {proposal.actions.includes("accept") && (
                <Button size="sm" onClick={onAccept} disabled={!valid}>
                  <Check className="mr-1 size-3" />
                  Accept
                </Button>
              )}
              {proposal.actions.includes("edit") && (
                <Button size="sm" variant="outline" disabled>
                  <Edit3 className="mr-1 size-3" />
                  Edit
                </Button>
              )}
              {proposal.actions.includes("reject") && (
                <Button size="sm" variant="ghost" onClick={onReject}>
                  <X className="mr-1 size-3" />
                  Reject
                </Button>
              )}
            </div>
          )}
          {decision && (
            <p className="text-[10px] font-medium uppercase tracking-wide text-on-surface-muted">
              {decision}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
